<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useDealNotesStore } from 'dashboard/stores/dealNotes';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactNoteItem from 'dashboard/components-next/Contacts/ContactsSidebar/components/ContactNoteItem.vue';

const props = defineProps({
  dealId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const dealNotesStore = useDealNotesStore();

const state = reactive({
  message: '',
});

const currentUser = useMapGetter('getCurrentUser');
const notes = computed(() => dealNotesStore.getNotesByDealId(props.dealId));
const uiFlags = computed(() => dealNotesStore.getUIFlags);
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);

const getWrittenBy = note => {
  const isCurrentUser = note?.user?.id === currentUser.value.id;
  return isCurrentUser
    ? t('DEALS.DETAIL.NOTES.YOU')
    : note?.user?.name || 'Bot';
};

const onAdd = content => {
  if (!content) return;
  dealNotesStore.create({ dealId: props.dealId, content });
  state.message = '';
};

const onDelete = noteId => {
  if (!noteId) return;
  dealNotesStore.delete({ noteId, dealId: props.dealId });
};

const keyboardEvents = {
  '$mod+Enter': {
    action: () => onAdd(state.message),
    allowOnFocusedInput: true,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Editor
      v-model="state.message"
      :placeholder="t('DEALS.DETAIL.NOTES.PLACEHOLDER')"
      class="[&>div]:!border-transparent [&>div]:px-4 [&>div]:py-4"
    >
      <template #actions>
        <div class="flex items-center gap-3">
          <Button
            variant="link"
            color="blue"
            size="sm"
            :label="t('DEALS.DETAIL.NOTES.SAVE')"
            class="hover:no-underline"
            :is-loading="isCreatingNote"
            :disabled="!state.message || isCreatingNote"
            @click="onAdd(state.message)"
          />
        </div>
      </template>
    </Editor>
    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="notes.length > 0">
      <ContactNoteItem
        v-for="note in notes"
        :key="note.id"
        class="py-4"
        :note="note"
        :written-by="getWrittenBy(note)"
        allow-delete
        @delete="onDelete"
      />
    </div>
    <p v-else class="py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('DEALS.DETAIL.NOTES.EMPTY_STATE') }}
    </p>
  </div>
</template>
